import React, { useEffect, useMemo, useState, useCallback } from "react";
import { Plus, Pencil, Trash2, Loader2, X, ChevronDown, Search } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { api, apiError } from "@/lib/api";

const CARTON_STATUSES = ["Pending", "Received", "Started", "In Progress", "Reviewed", "Completed"];

const STATUS_BADGE = {
  "Pending": "bg-slate-100 text-slate-700",
  "Received": "bg-sky-100 text-sky-800",
  "Started": "bg-indigo-100 text-indigo-800",
  "In Progress": "bg-amber-100 text-amber-800",
  "Reviewed": "bg-violet-100 text-violet-800",
  "Completed": "bg-emerald-100 text-emerald-800",
};

// Parse "T-004-0091" -> {prefix:"T-004-", num:91, width:4}
function parseSerial(s) {
  if (!s) return null;
  const m = /^(.*?)(\d+)$/.exec(s.trim());
  if (!m) return null;
  return { prefix: m[1], num: parseInt(m[2], 10), width: m[2].length };
}
function formatSerial(prefix, num, width) {
  return `${prefix}${String(num).padStart(width, "0")}`;
}

function StatCard({ label, value, sub, tone }) {
  const toneClass = tone === "red" ? "text-rose-600" : tone === "green" ? "text-emerald-600" : "text-slate-900";
  return (
    <Card className="border-slate-200">
      <CardContent className="p-5">
        <p className="text-xs font-semibold uppercase tracking-wider text-slate-500">{label}</p>
        <p className={`mt-2 text-3xl font-bold tracking-tight ${toneClass}`}>{value}</p>
        {sub && <p className="mt-1 text-xs text-slate-500">{sub}</p>}
      </CardContent>
    </Card>
  );
}

export default function CartonTracker({ canWrite }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [missingOnly, setMissingOnly] = useState(false);
  const [editing, setEditing] = useState(null); // null | "new" | carton object
  const [toDelete, setToDelete] = useState(null);
  const [expanded, setExpanded] = useState({});

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/cartons");
      setItems(data || []);
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => {
    const query = q.trim().toLowerCase();
    return items.filter((c) => {
      if (missingOnly && (c.missing_count || 0) === 0) return false;
      if (!query) return true;
      if (String(c.carton_number).includes(query)) return true;
      if ((c.start_stick || "").toLowerCase().includes(query)) return true;
      if ((c.end_stick || "").toLowerCase().includes(query)) return true;
      if ((c.missing_sticks || []).some((s) => (s || "").toLowerCase().includes(query))) return true;
      return false;
    });
  }, [items, q, missingOnly]);

  const totalCartons = items.length;
  const totalExpected = items.reduce((s, c) => s + (c.expected || 0), 0);
  const totalMissing = items.reduce((s, c) => s + (c.missing_count || 0), 0);
  const totalReceived = Math.max(0, totalExpected - totalMissing);

  return (
    <div className="space-y-6">
      <div className="grid gap-4 sm:grid-cols-3">
        <StatCard label="Total Cartons" value={totalCartons} sub="total cartons" />
        <StatCard label="Sticks Received" value={totalReceived} sub={`of ${totalExpected} expected`} tone="green" />
        <StatCard label="Missing Sticks" value={totalMissing} sub="across all cartons" tone={totalMissing > 0 ? "red" : undefined} />
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <div className="relative min-w-[240px] flex-1">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <Input
            className="pl-9"
            placeholder="Search carton # or stick serial…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            data-testid="cartons-search"
          />
        </div>
        <label className="flex items-center gap-2 rounded-md border border-slate-200 bg-white px-3 py-2 text-sm">
          <Checkbox
            checked={missingOnly}
            onCheckedChange={(v) => setMissingOnly(!!v)}
            data-testid="cartons-missing-only"
          />
          Missing sticks only
        </label>
        <span className="text-sm text-muted-foreground">{filtered.length} of {items.length} cartons</span>
        {canWrite && (
          <Button size="sm" onClick={() => setEditing("new")} data-testid="cartons-add-btn">
            <Plus className="mr-1 h-4 w-4" /> Add Carton
          </Button>
        )}
      </div>

      {loading ? (
        <p className="text-muted-foreground">Loading cartons…</p>
      ) : filtered.length === 0 ? (
        <div className="rounded-lg border border-dashed border-slate-300 bg-slate-50 p-10 text-center text-muted-foreground">
          {items.length === 0 ? "No cartons yet. Click Add Carton to get started." : "No cartons match your filters."}
        </div>
      ) : (
        <div className="space-y-3">
          {filtered.map((c) => {
            const isOpen = !!expanded[c.id];
            return (
              <Card key={c.id} className="border-slate-200" data-testid={`carton-row-${c.carton_number}`}>
                <button
                  onClick={() => setExpanded((s) => ({ ...s, [c.id]: !s[c.id] }))}
                  className="flex w-full items-center justify-between gap-4 px-5 py-4 text-left transition hover:bg-slate-50"
                >
                  <div className="flex items-center gap-4">
                    <span className="rounded-md bg-slate-900 px-2 py-1 font-mono text-xs font-semibold text-white">C{String(c.carton_number).padStart(2, "0")}</span>
                    <div>
                      <p className="font-semibold text-slate-900">Carton {c.carton_number}</p>
                      <p className="mt-0.5 font-mono text-xs text-slate-500">{c.start_stick} → {c.end_stick}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="text-right text-sm">
                      <p className="font-medium text-slate-900">{c.actual_count} / {c.expected} sticks</p>
                      {c.missing_count > 0 && <p className="text-xs text-rose-600">{c.missing_count} missing</p>}
                    </div>
                    <Badge className={`${STATUS_BADGE[c.status] || "bg-slate-100"} hover:opacity-90`}>{c.status}</Badge>
                    <ChevronDown className={`h-4 w-4 text-slate-400 transition-transform ${isOpen ? "rotate-180" : ""}`} />
                  </div>
                </button>
                {isOpen && (
                  <div className="border-t border-slate-100 bg-slate-50/50 px-5 py-4 text-sm">
                    {c.missing_sticks?.length > 0 && (
                      <div className="mb-3">
                        <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Missing sticks</p>
                        <div className="mt-1 flex flex-wrap gap-1.5">
                          {c.missing_sticks.map((s) => (
                            <span key={s} className="rounded-md bg-rose-50 px-2 py-0.5 font-mono text-xs text-rose-700">{s}</span>
                          ))}
                        </div>
                      </div>
                    )}
                    {c.notes && (
                      <div className="mb-3">
                        <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Notes</p>
                        <p className="mt-1 text-slate-700 whitespace-pre-wrap">{c.notes}</p>
                      </div>
                    )}
                    {canWrite && (
                      <div className="flex gap-2">
                        <Button size="sm" variant="outline" onClick={() => setEditing(c)} data-testid={`carton-edit-${c.carton_number}`}>
                          <Pencil className="mr-1 h-3.5 w-3.5" /> Edit
                        </Button>
                        <Button size="sm" variant="destructive" onClick={() => setToDelete(c)} data-testid={`carton-delete-${c.carton_number}`}>
                          <Trash2 className="mr-1 h-3.5 w-3.5" /> Delete
                        </Button>
                      </div>
                    )}
                  </div>
                )}
              </Card>
            );
          })}
        </div>
      )}

      {editing && canWrite && (
        <CartonForm carton={editing === "new" ? null : editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />
      )}

      <Dialog open={!!toDelete} onOpenChange={(o) => !o && setToDelete(null)}>
        <DialogContent data-testid="carton-delete-dialog">
          <DialogHeader>
            <DialogTitle>Delete Carton {toDelete?.carton_number}?</DialogTitle>
            <DialogDescription>
              This carton and its missing-stick list will be permanently removed. This cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setToDelete(null)}>Cancel</Button>
            <Button
              variant="destructive"
              onClick={async () => {
                try { await api.delete(`/cartons/${toDelete.id}`); setToDelete(null); load(); }
                catch (err) { alert(apiError(err)); }
              }}
              data-testid="carton-delete-confirm"
            >
              Delete Carton
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ------------------------------------------------------------------
// Carton slide-over form
// ------------------------------------------------------------------

function CartonForm({ carton, onClose, onSaved }) {
  const isEdit = !!carton;
  const [cartonNumber, setCartonNumber] = useState(carton?.carton_number || "");
  const [startStick, setStartStick] = useState(carton?.start_stick || "");
  const [endStick, setEndStick] = useState(carton?.end_stick || "");
  const [status, setStatus] = useState(carton?.status || "Pending");
  const [missingText, setMissingText] = useState((carton?.missing_sticks || []).join(", "));
  const [notes, setNotes] = useState(carton?.notes || "");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const preview = useMemo(() => {
    const ps = parseSerial(startStick);
    const pe = parseSerial(endStick);
    if (!ps || !pe) return null;
    if (ps.prefix !== pe.prefix) return { error: "Start and End Stick must share the same prefix." };
    if (pe.num < ps.num) return { error: "End Stick must be ≥ Start Stick." };
    return {
      prefix: ps.prefix, width: ps.width, startNum: ps.num, endNum: pe.num,
      expected: pe.num - ps.num + 1,
    };
  }, [startStick, endStick]);

  const missingParsed = useMemo(() => {
    const clean = missingText.split(/[,\s]+/).map((s) => s.trim()).filter(Boolean);
    if (!preview || preview.error) return { list: clean, foreign: [] };
    const foreign = clean.filter((s) => {
      const p = parseSerial(s);
      if (!p) return true;
      if (p.prefix !== preview.prefix) return true;
      if (p.num < preview.startNum || p.num > preview.endNum) return true;
      return false;
    });
    return { list: clean, foreign };
  }, [missingText, preview]);

  async function submit() {
    if (!cartonNumber || cartonNumber < 1) { setError("Carton Number is required."); return; }
    if (!startStick || !endStick) { setError("Start and End Stick are required."); return; }
    if (preview?.error) { setError(preview.error); return; }
    setSaving(true); setError(null);
    try {
      const payload = {
        carton_number: parseInt(cartonNumber, 10),
        start_stick: startStick.trim(),
        end_stick: endStick.trim(),
        status,
        missing_sticks: missingParsed.list,
        notes: notes.trim() || null,
      };
      if (isEdit) await api.put(`/cartons/${carton.id}`, payload);
      else await api.post("/cartons", payload);
      onSaved();
    } catch (err) { setError(apiError(err)); }
    finally { setSaving(false); }
  }

  return (
    <Sheet open onOpenChange={(o) => !o && onClose()}>
      <SheetContent side="right" className="w-full overflow-y-auto sm:max-w-md" data-testid="carton-form-sheet">
        <SheetHeader>
          <SheetTitle>{isEdit ? "Edit Carton" : "Add Carton"}</SheetTitle>
        </SheetHeader>
        <div className="mt-6 space-y-5">
          <div>
            <label className="text-sm font-medium">Carton Number *</label>
            <Input
              type="number"
              min={1}
              value={cartonNumber}
              onChange={(e) => setCartonNumber(e.target.value)}
              className="mt-1"
              data-testid="carton-form-number"
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-sm font-medium">Start Stick *</label>
              <Input
                value={startStick}
                onChange={(e) => setStartStick(e.target.value)}
                placeholder="T-004-0001"
                className="mt-1 font-mono"
                data-testid="carton-form-start"
              />
            </div>
            <div>
              <label className="text-sm font-medium">End Stick *</label>
              <Input
                value={endStick}
                onChange={(e) => setEndStick(e.target.value)}
                placeholder="T-004-0050"
                className="mt-1 font-mono"
                data-testid="carton-form-end"
              />
            </div>
          </div>
          {preview && (
            <div className="rounded-lg bg-slate-50 p-4 text-sm">
              <p className="font-semibold text-slate-700">Range preview</p>
              {preview.error ? (
                <p className="mt-1 text-rose-600">{preview.error}</p>
              ) : (
                <>
                  <p className="mt-1 font-mono text-slate-600">
                    {formatSerial(preview.prefix, preview.startNum, preview.width)} → {formatSerial(preview.prefix, preview.endNum, preview.width)}
                  </p>
                  <p className="mt-1 text-slate-600">Expected: <span className="font-semibold text-slate-900">{preview.expected} sticks</span></p>
                  <p className="text-rose-600">Missing: <span className="font-semibold">{missingParsed.list.length}</span></p>
                  <p className="text-emerald-600">Actual count: <span className="font-semibold">{Math.max(0, preview.expected - missingParsed.list.length)}</span></p>
                </>
              )}
            </div>
          )}
          <div>
            <label className="text-sm font-medium">Missing Sticks</label>
            <Input
              value={missingText}
              onChange={(e) => setMissingText(e.target.value)}
              placeholder="T-004-0005, T-004-0012"
              className="mt-1 font-mono"
              data-testid="carton-form-missing"
            />
            <p className="mt-1 text-xs text-slate-500">Enter missing stick serials, comma-separated.</p>
            {missingParsed.foreign.length > 0 && (
              <p className="mt-1 text-xs font-medium text-rose-600" data-testid="carton-form-foreign-warning">
                Foreign stick — not in this carton's range: {missingParsed.foreign.join(", ")}
              </p>
            )}
          </div>
          <div>
            <label className="text-sm font-medium">Status</label>
            <select
              className="mt-1 flex h-9 w-full rounded-md border border-slate-200 bg-white px-3 text-sm shadow-sm focus:outline-none focus:ring-1 focus:ring-slate-400"
              value={status}
              onChange={(e) => setStatus(e.target.value)}
              data-testid="carton-form-status"
            >
              {CARTON_STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
          <div>
            <label className="text-sm font-medium">Notes</label>
            <Textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Any notes about this carton…"
              className="mt-1"
              rows={3}
              data-testid="carton-form-notes"
            />
          </div>
          {error && <p className="rounded-md bg-rose-50 px-3 py-2 text-sm text-rose-700">{error}</p>}
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" onClick={onClose} disabled={saving}><X className="mr-1 h-4 w-4" /> Cancel</Button>
            <Button onClick={submit} disabled={saving} data-testid="carton-form-submit">
              {saving && <Loader2 className="mr-1 h-4 w-4 animate-spin" />}
              {isEdit ? "Save Carton" : "Add Carton"}
            </Button>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}
